// Product catalogue GLB loader — DECISION-023 Phase 4 / AAA Engine v2 scope.
//
// Mirror of `_fixture-catalogue-loader.js` for product GLB templates.
// boot.json.productCatalogue maps a kapowxProductType to a CDN-resolved
// geometryAssetKey. This loader fetches + caches the catalogue GLB once
// per (type, librarySha) so multiple instances of the same product
// type share one network fetch.
//
// Resolver semantics:
//   1. If catalogue[type].geometryAssetKey present AND VITE_SCENE_CDN_URL
//      set → load from `${VITE_SCENE_CDN_URL}/${geometryAssetKey}`.
//   2. Else → return null. Caller falls back to today's procedural
//      textured-plane product rendering.
//
// Same Vite-substitution gotcha as the fixture-catalogue loader:
// MUST use exact `import.meta.env.VITE_SCENE_CDN_URL` pattern.

const CDN_BASE = (() => {
	try {
		const raw = import.meta.env.VITE_SCENE_CDN_URL;
		if (typeof raw === "string" && raw.length > 0) return raw.replace(/\/$/, "");
	} catch { /* not under Vite */ }
	return null;
})();

const CACHE = new Map();

/**
 * Resolve the catalogue GLB URL for a product type.
 *
 * @param {Record<string, { geometryAssetKey?: string }> | null | undefined} catalogue
 * @param {string} productType — kapowxProductType, e.g., "comic-modern"
 * @returns {string | null}
 */
export function resolveProductCatalogueUrl(catalogue, productType) {
	const entry = catalogue?.[productType];
	const key = entry?.geometryAssetKey;
	if (typeof key !== "string" || key.length === 0) return null;
	if (!CDN_BASE) return null;
	return `${CDN_BASE}/${key}`;
}

/**
 * Load + cache the product-type catalogue GLB. Returns the gltf
 * object ({ scene, scenes, ... }) or null when no catalogue entry.
 *
 * @param {object} args
 * @param {Record<string, { geometryAssetKey?: string }> | null | undefined} args.catalogue
 * @param {string} args.productType
 * @param {{ loadAsync: (url: string) => Promise<any> }} args.gltfLoader — pass stage.gltfLoader
 * @returns {Promise<any | null>}
 */
export async function loadProductCatalogue({ catalogue, productType, gltfLoader }) {
	const url = resolveProductCatalogueUrl(catalogue, productType);
	if (!url) return null;
	let pending = CACHE.get(url);
	if (!pending) {
		pending = (async () => {
			try {
				const gltf = await gltfLoader.loadAsync(url);
				console.log(`[product-catalogue] loaded ${productType} from ${url}`);
				return gltf;
			} catch (err) {
				console.warn(`[product-catalogue] failed to load ${productType} from ${url}:`, err);
				return null;
			}
		})();
		CACHE.set(url, pending);
	}
	return pending;
}

// Per-type manifest loader (mirror of fixture manifest-loader, scoped
// to products). Reads boot.json.productCatalogue[type].manifestAssetKey.
const MANIFEST_CACHE = new Map();

export async function loadProductManifest({ catalogue, productType }) {
	const entry = catalogue?.[productType];

	// Inline manifest (rare; Forge may compose into boot.json directly).
	if (entry?.manifest && typeof entry.manifest === "object") {
		return entry.manifest;
	}

	// CDN sidecar.
	const key = entry?.manifestAssetKey;
	if (typeof key !== "string" || key.length === 0 || !CDN_BASE) return null;
	const url = `${CDN_BASE}/${key}`;
	let pending = MANIFEST_CACHE.get(url);
	if (!pending) {
		pending = (async () => {
			try {
				const r = await fetch(url, { cache: "default" });
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				const manifest = await r.json();
				console.log(`[product-manifest] loaded ${productType} from ${url}`);
				return manifest;
			} catch (err) {
				console.warn(`[product-manifest] failed to load ${productType} from ${url}:`, err);
				return null;
			}
		})();
		MANIFEST_CACHE.set(url, pending);
	}
	return pending;
}

// ─── Which mesh does a product seat draw? (P2-E2 ruling 2, coord 2026-09-02) ─────────────────
//
// Two-step rule, refusing loudly:
//   1. concrete type    → productCatalogue[type]           (DECISION-023's own semantics)
//   2. else the line's  `renderAssetKey`, a CATALOGUE KEY — a productCatalogue key GCG emits
//      from product_asset_binding (a SKU's art rides the SKU, Shopify variant media), OR a
//      fixtureCatalogue key for a non-sellable decor line (ASK-126: a decor asset IS its key,
//      and D-063's one-asset-class rule means "any catalogue-keyed mesh", not one map of two —
//      the 11 shipped decor kinds live in the fixture catalogue). Absent on every unbound line
//      (D-061 additive-and-inert).
//   3. neither, and the type is not the comic family → the seat renders NOTHING and says why
//      BY NAME (`no-render-asset`) — never a guessed placeholder. A line that DECLARES a
//      renderAssetKey resolving nowhere is refused even untyped: it named its mesh source and
//      the name is broken, which is a different fact from never having named one.
//
// The comic FAMILY keeps its procedural template (textured with the SKU's own covers): that is
// its real render path today, not a placeholder, and every live product on the platform rides
// it. An untyped legacy line with no renderAssetKey, or a registry that has not loaded,
// resolves the way it always did — absent is "no opinion", never a refusal (WoW §6).

const COMIC_TAG = "product.media.print.comic";

/**
 * @returns {{kind:"catalogue", key:string, source:"product"|"fixture"} | {kind:"comic-template"}
 *        | {kind:"refused", reason:"no-render-asset", detail:string}}
 */
export function resolveProductRenderSource({ catalogue = null, fixtureCatalogue = null, productTypes = null, product = null } = {}) {
	const hasGeometry = (map, key) => typeof map?.[key]?.geometryAssetKey === "string" && map[key].geometryAssetKey;
	const type = product?.type ?? product?.kapowxProductType ?? null;
	if (type && hasGeometry(catalogue, type)) {
		return { kind: "catalogue", key: type, source: "product" };
	}
	const rak = typeof product?.renderAssetKey === "string" && product.renderAssetKey ? product.renderAssetKey : null;
	if (rak && hasGeometry(catalogue, rak)) {
		return { kind: "catalogue", key: rak, source: "product" };
	}
	if (rak && hasGeometry(fixtureCatalogue, rak)) {
		return { kind: "catalogue", key: rak, source: "fixture" };
	}
	// A broken rak refuses only where no other real path exists: a typed comic still has its own
	// procedural template (unchanged), but a line whose ONLY named mesh source resolves nowhere is
	// refused by that name rather than guessed into a comic.
	const rakBroken = rak ? {
		kind: "refused", reason: "no-render-asset",
		detail: `renderAssetKey '${rak}' names no catalogue entry with geometry (product or fixture)`,
	} : null;
	if (!type) return rakBroken ?? { kind: "comic-template" };
	const entry = (Array.isArray(productTypes) ? productTypes : []).find((t) => t?.productTypeId === type);
	if (!entry) return { kind: "comic-template" };
	const tagPath = entry.tagPath || "";
	if (tagPath === COMIC_TAG || tagPath.startsWith(COMIC_TAG + ".")) return { kind: "comic-template" };

	// ─── ASK-128 F-H(2) — A `both` ASSET RENDERS FROM ITS OWN PUBLISHED MESH ────────────────────
	//
	// The piano is the platform's first `both` asset: a FIXTURE KIND that is also SELLABLE. Seated as
	// stock it reached this line and was refused — `productCatalogue` carries 10 keys and none of them
	// is furniture (measured on the live boot) — so E5's headline gesture would have placed a piano
	// that drew nothing.
	//
	// 🔴 THE KEY IS THE ASSET'S OWN NAME, IN WHICHEVER CATALOGUE PUBLISHED IT. Measured 2026-09-04:
	// `fixtureCatalogue["upright-piano"]` exists WITH a `geometryAssetKey`, and `productTypes` carries
	// `upright-piano` as a real type. So the mesh was addressable the whole time and the resolver
	// simply never looked: it keyed the fixture catalogue off `renderAssetKey` only, and the feed
	// carries none for a piano (3 of 15 rows carry one at all, and those are the decor path).
	//
	// That decor path is also the PROOF this works: `source:"fixture"` already loads a GLB out of the
	// fixture catalogue through the same loader, for the same reason — "the key resolves in whichever
	// catalogue owns it, the loader is key→GLB either way, one renderer, one path" (ASK-126). This
	// branch adds the OTHER way of naming the same asset: by its type rather than by a per-row pointer.
	//
	// This is D-063's one-class composition stated as code: ONE asset, ONE mesh, TWO roles. The bytes
	// are never duplicated into `products/`, and no publish is owed by Content. A per-row
	// `renderAssetKey` remains the FALLBACK and still works unchanged (branch 3 above) — so GCG owes
	// nothing either: emit it or don't, both resolve to the same mesh by the same key.
	//
	// Placed LAST on purpose. Every existing path is decided above it, so this can only ever turn a
	// REFUSAL into a render — a real `productCatalogue` entry always wins, and the comic family keeps
	// its template. A type that names no fixture kind still refuses, by the same name it did before.
	if (hasGeometry(fixtureCatalogue, type)) {
		return { kind: "catalogue", key: type, source: "fixture", both: true };
	}

	return rakBroken ?? {
		kind: "refused", reason: "no-render-asset",
		detail: `type '${type}' has no productCatalogue entry, no fixtureCatalogue mesh of its own, and the line carries no renderAssetKey`,
	};
}

export function _resetProductCatalogueCache() {
	CACHE.clear();
	MANIFEST_CACHE.clear();
}
